import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { AnalysisCategoryItem } from "./AnalysisCategoryItem";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

interface AnalysisCategoriesListProps {
  categories: {
    title: string;
    score?: number;
    content: string | string[] | object;
    key: string;
    group?: string;
  }[];
  searchQuery: string;
}

export const AnalysisCategoriesList = ({ categories, searchQuery }: AnalysisCategoriesListProps) => {
  // Helper function to safely convert content to string for searching
  const contentToSearchableString = (content: any): string => {
    if (content === null || content === undefined) {
      return '';
    }
    
    if (typeof content === 'string') {
      return content.toLowerCase();
    }
    
    if (Array.isArray(content)) {
      return content.map(item => contentToSearchableString(item)).join(' ');
    }
    
    if (typeof content === 'object') {
      return Object.values(content).map(val => contentToSearchableString(val)).join(' ');
    }
    
    return String(content).toLowerCase();
  };

  // Filter categories based on search query
  const filteredCategories = searchQuery 
    ? categories.filter(category => {
        const titleMatch = category.title.toLowerCase().includes(searchQuery.toLowerCase());
        const contentMatch = contentToSearchableString(category.content).includes(searchQuery.toLowerCase());
        return titleMatch || contentMatch;
      })
    : categories;

  // Group the categories
  const groupedCategories: Record<string, typeof filteredCategories> = {};

  filteredCategories.forEach(category => {
    const group = category.group || "Other";
    if (!groupedCategories[group]) {
      groupedCategories[group] = [];
    }
    groupedCategories[group].push(category);
  });

  return (
    <ScrollArea className="h-full px-4">
      <div className="py-2 space-y-2">
        {Object.keys(groupedCategories).length > 0 ? (
          <Accordion type="multiple" className="w-full space-y-4">
            {Object.entries(groupedCategories).map(([group, cats]) => (
              <AccordionItem 
                key={group} 
                value={group} 
                className="border rounded-md overflow-hidden shadow-sm hover:shadow-md transition-shadow bg-white"
              >
                <AccordionTrigger className="px-4 py-3 font-semibold text-gray-700 hover:bg-gray-50">
                  {group}
                </AccordionTrigger>
                <AccordionContent className="pb-1">
                  <div className="px-4 space-y-4 mb-4">
                    {cats.map((category, index) => (
                      <AnalysisCategoryItem key={index} category={category} />
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        ) : (
          <div className="text-center text-gray-500 py-8">
            {searchQuery ? "No matching criteria found" : "No detailed analysis available"}
          </div>
        )}
      </div>
    </ScrollArea>
  );
};